import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import React, { memo } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import TruncatedText from "../../../../components/common/TruncatedText";
import Button from "../../../../components/form/Button";
import { ROUTE_TEMPLATES_EDIT } from "../../../../constants/routes";
import { SourceEnum } from "../../../../enums/template";
import useSelectedAccount from "../../../../hooks/useSelectedAccount";
import { ArticleProps } from "../../../../models/article";
import {
  browserTimeZone,
  formatDateUS,
  formatNumberWithCommas,
  formatTimeUS,
  getEngineName,
  getEngineSVG,
  getImageEngineName,
  getImageEngineSVG,
  getSourceSVG,
} from "../../../../utils";
import ArticleActions from "../ArticleActions";
import ArticleCopy from "../ArticleCopy";
import ArticleStatus from "../ArticleStatus";

export const TitleCell = memo<{
  article: ArticleProps;
  onAction: Function;
}>(function TitleCell({ article, onAction }) {
  const click = () => {
    if (article.s3ArticleUrl) {
      onAction(article.articleId, "view");
    }
  };

  return (
    <div
      className={cn("flex items-center gap-3 truncate overflow-hidden", {
        "text-red-500": article.generateError,
        "cursor-pointer text-primary": article.s3ArticleUrl,
      })}
      onClick={click}
    >
      <img
        alt="source logo"
        src={getSourceSVG(article.sourceId)}
        className="size-5"
      />
      <TruncatedText
        id={`article-name-${article.articleId}`}
        text={article.title}
      />
    </div>
  );
});

export const SourceCell = memo<{
  article: ArticleProps;
  onAction: Function;
}>(function SourceCell({ article, onAction }) {
  if (
    article.sourceId === SourceEnum.TOP_ITEMS ||
    article.sourceId === SourceEnum.BEST_ITEMS
  ) {
    // researched, not sourced from a video/audio link: no "open source" arrow to show
    return <div className="md:text-center">–</div>;
  }

  return (
    <div className="flex items-center">
      <a
        href={article.mediaUrl}
        target="_blank"
        rel="noreferrer"
        className="group"
      >
        <Button
          tooltip="Open source"
          variant="icon"
          onClick={() => {}}
        >
          <ArrowTopRightOnSquareIcon className="size-4" />
        </Button>
      </a>
      <ArticleCopy
        mediaUrl={article.mediaUrl}
        articleId={article.articleId}
      />
      {article.s3TranscriptionUrl && (
        <Button
          tooltip="View transcript"
          variant="icon"
          onClick={() => onAction(article.articleId, "transcript")}
        >
          <DocumentTextIcon className="size-3.5 text-gray-600" />
        </Button>
      )}
    </div>
  );
});

export const TemplateCell = memo<{ article: ArticleProps }>(
  function TemplateCell({ article }) {
    const navigate = useNavigate();
    return (
      <TruncatedText
        id={`template-name-${article.articleId}`}
        text={article.templateName}
        className="text-primary cursor-pointer truncate max-w-xs"
        onClick={() => {
          navigate(
            generatePath(ROUTE_TEMPLATES_EDIT, {
              id: article.templateId,
            })
          );
        }}
      />
    );
  }
);

export const WordsCell = memo<{ article: ArticleProps }>(function WordsCell({
  article,
}) {
  return <span>{formatNumberWithCommas(article.articleWordCount) || "–"}</span>;
});

export const TokensCell = memo<{ article: ArticleProps }>(function TokensCell({
  article,
}) {
  return <span>{formatNumberWithCommas(article.tokensUsed) || "–"}</span>;
});

export const EngineCell = memo<{ article: ArticleProps }>(function EngineCell({
  article,
}) {
  return article.engineId ? (
    <div className="flex items-center gap-1">
      <img
        alt="engine logo"
        src={getEngineSVG(article.engineId)}
        className="w-5 h-5"
      />
      {getEngineName(article.engineId)}
    </div>
  ) : (
    <span>–</span>
  );
});

export const ImageEngineCell = memo<{ article: ArticleProps }>(
  function ImageEngineCell({ article }) {
    return article.imageEngineId ? (
      <div className="flex items-center gap-1">
        <img
          alt="engine-logo"
          src={getImageEngineSVG(article.imageEngineId)}
          className="w-5 h-5"
        />
        {getImageEngineName(article.imageEngineId)}
      </div>
    ) : (
      <span className="flex md:justify-center">–</span>
    );
  }
);

export const StatusCell = memo<{ article: ArticleProps }>(function StatusCell({
  article,
}) {
  return (
    <ArticleStatus
      articleStatusId={article.articleStatusId}
      generateError={article.generateError}
    />
  );
});

export const CreatedCell = memo<{ article: ArticleProps }>(
  function CreatedCell({ article }) {
    const { selectedAccount } = useSelectedAccount();
    const timezone = selectedAccount?.timezone ?? browserTimeZone;
    const date = formatDateUS(article.createdAt, true, "short", timezone);
    const time = formatTimeUS(article.createdAt, false, timezone);

    return (
      <div className="flex flex-col">
        <span>{date}</span>
        <time className="mt-0.5 text-xs text-gray-500">{time}</time>
      </div>
    );
  }
);

export const ActionsCell = memo<{
  article: ArticleProps;
  onAction: Function;
}>(function ActionsCell({ article, onAction }) {
  if (!article.s3ArticleUrl) return null;
  return (
    <div className="flex justify-end">
      <ArticleActions
        articleId={article.articleId}
        articleUrl={article.s3ArticleUrl}
        onView={() => onAction(article.articleId, "view")}
      />
    </div>
  );
});

export const PostedCell = memo<{ article: ArticleProps }>(function PostedCell({
  article,
}) {
  return (
    <div className="flex items-center justify-end space-x-2">
      {/* Date & Time */}

      {article?.postedAt && <PostedAt postedAt={article.postedAt} />}
      {/* External-link icon only if URL is provided */}
      {article.postedUrl && (
        <a
          href={article.postedUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Open posted URL"
        >
          <Button
            tooltip="Open posted URL"
            variant="icon"
            onClick={() => {}}
          >
            <ArrowTopRightOnSquareIcon className="size-4" />
          </Button>
        </a>
      )}
    </div>
  );
});

const PostedAt = ({ postedAt }: { postedAt: string }) => {
  const { selectedAccount } = useSelectedAccount();

  const timezone = selectedAccount?.timezone ?? browserTimeZone;
  const date = formatDateUS(postedAt, true, "short", timezone);
  const time = formatTimeUS(postedAt, false, timezone);
  return (
    <div className="flex flex-col">
      <span>{date}</span>
      <time className="mt-0.5 text-xs text-gray-500">{time}</time>
    </div>
  );
};
